import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import ResolutionCohabitation from 'mutation/components/resolution-facto-cohabitation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResolutionFactoCohabitationFormVM } from 'mutation/view-models/resolution-facto-cohabitation';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { transformerResolutionFactoCohabitationData } from 'mutation/utils/transformer-resolution-facto-cohabitation';
import ExitModal from 'mutation/components/shared/exit-modal';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformerRequestResolutionFactoCohabitationData } from 'mutation/utils/transformer-request-resolution-facto-cohabitation';
import { toast } from 'react-toastify';
import { validationSchema } from 'mutation/constants/resolution-facto-cohabitation-validation';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { ResolutionFactoCohabitationCrumbs } from 'mutation/constants/resolution-facto-cohabitation-crumbs';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import PmiTitle from 'shell/components/shared/title';
import ButtonsResolutionCohabitation from 'mutation/components/resolution-facto-cohabitation/buttons';

const ResolutionFactoCohabitationPage = (): React.ReactElement => {
  //Modal state
  const [open, setOpen] = useState<boolean>(false);

  const [queryParams] = useSearchParams();
  // query params
  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, ResolutionFactoCohabitationFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: transformerResolutionFactoCohabitationData,
    }
  );

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [responseCohabitation, declareCohabitation] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/risoluzione-convivenza-di-fatto`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedAnprData = responseCohabitation.data as SaveDataAnpr;

  const onVisibility = useCallback(
    (openVisibility, type) => {
      const visibilityMessage = type === 'error' && !openVisibility ? undefined : openVisibility;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (
      castedAnprData?.errors?.length &&
      castedAnprData?.errors?.length > 0 &&
      visibility.error === undefined
    ) {
      errorToVisualize.title =
        castedAnprData?.errors?.length > 1
          ? `Ci sono ${castedAnprData?.errors?.length} errori`
          : `C'è 1 errore`;
      // errorToVisualize.title = `Ci sono ${(anpr.data as AnprVM<PersonVM>)?.errors?.length} errori`;
      errorToVisualize.messages = castedAnprData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedAnprData?.errors]);

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (values) => {
      const data = transformerRequestResolutionFactoCohabitationData(values);

      (
        declareCohabitation({ data }) as ResponsePromise<
          HttpResponse<ResponseAnprSaveData>,
          SaveDataAnpr
        >
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;

        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }
        toast.success('Operazione eseguita con successo');
        navigate('/mutazioni/persona/convivenza');
      });
    },
    [declareCohabitation, navigate]
  );

  const handleExit = useCallback(() => {
    navigate('/mutazioni/persona/convivenza');
  }, [navigate]);

  const isLoading = anpr.isLoading || anpr.pristine;

  return !isLoading ? (
    <Fragment>
      <Breadcrumbs items={ResolutionFactoCohabitationCrumbs} />
      <PmiTitle title="Risoluzione Convivenza di fatto" />
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={
          castedAnprData?.warnings?.length === 1
            ? `C'è 1 warning`
            : `Ci sono ${castedAnprData?.warnings?.length} warning`
        }
        messages={castedAnprData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedAnprData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      <Formik
        validationSchema={validationSchema}
        initialValues={(anpr.data as ResolutionFactoCohabitationFormVM) || {}}
        onSubmit={handleSubmit}
      >
        <Form>
          <Grid container spacing={1}>
            <Grid item xs={12} md={2}>
              <ButtonsResolutionCohabitation openModal={setOpen} />
            </Grid>
            <Grid item xs={12} md={10}>
              <Grid container spacing={1} item xs={12}>
                <Grid item xs={12}>
                  <ResolutionCohabitation />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <ExitModal open={open} onClose={() => setOpen(false)} onConfirm={handleExit} />
        </Form>
      </Formik>
    </Fragment>
  ) : (
    <></>
  );
};

ResolutionFactoCohabitationPage.displayName = 'Resolution Cohabitation page component';

export default ResolutionFactoCohabitationPage;
